import { auth, db } from '@/firebase'
import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore'
import { EmailService } from './emailService'

/**
 * Task Manager for handling scheduled tasks client-side
 * Replaces Cloud Functions for recurring invoices, reminders, etc.
 * Uses existing app services instead of creating duplicates
 */
class TaskManager {
  constructor() {
    this.worker = null
    this.isRunning = false
    this.taskInterval = 60000 // Check every minute
    this.emailService = new EmailService()
    // logging guard to avoid repeated write attempts when rules deny writes
    this._logWriteBlocked = false
    this._lastLogAttempt = 0
    // runtime override to allow system writes from client for dev/testing
    // set `window.__TASK_MANAGER_ALLOW_WRITES = true` in browser console to enable
    this._allowSystemWritesOverride = (typeof window !== 'undefined' && window.__TASK_MANAGER_ALLOW_WRITES) || false
  }

  _canWriteSystem() {
    // Prefer explicit runtime override to enable system writes.
    // This avoids accidental permission-denied write storms in production.
    if (this._allowSystemWritesOverride) return true
    // In node/test environments allow writes (helps unit tests that mock Firestore)
    if (typeof process !== 'undefined' && process.env && process.env.VITEST) return true
    return false
  }

  /**
   * Check if current user is super admin
   */
  _isSuperAdmin() {
    const user = auth.currentUser
    if (!user) return false
    const adminEmail = process.env.VUE_APP_ADMIN_EMAIL || 'collinsodeny@gmail.com'
    return user.email === adminEmail
  }

  /**
   * Start the task manager
   */
  start() {
    if (this.isRunning) return

    this.isRunning = true
    // Initial check
    this.checkTasks()

    // Set up interval for continuous checking
    this.worker = setInterval(() => {
      this.checkTasks()
    }, this.taskInterval)
  }

  /**
   * Stop the task manager
   */
  stop() {
    if (this.worker) {
      clearInterval(this.worker)
      this.worker = null
    }
    this.isRunning = false
  }

  /**
   * Check for and process overdue tasks
   */
  async checkTasks() {
    // Only super admin should process system tasks to avoid permission errors
    if (!this._isSuperAdmin() && !this._canWriteSystem()) {
      return
    }

    try {
      await this.processRecurringInvoices()
      await this.processPaymentReminders()
      await this.updateLastTaskCheck()
    } catch (error) {
      console.error('Error processing tasks:', error)
      this.logError('task_processing_error', error)
    }
  }

  /**
   * Process recurring invoices that are due
   */
  async processRecurringInvoices() {
    const now = new Date()
    const recurrencesRef = collection(db, 'organizations')

    // Query all organizations (super admin can access all)
    const orgsSnapshot = await getDocs(recurrencesRef)

    for (const orgDoc of orgsSnapshot.docs) {
      const orgId = orgDoc.id
      const recurrencesQuery = query(
        collection(db, `organizations/${orgId}/recurrences`),
        where('active', '==', true),
        where('nextRunAt', '<=', now.getTime())
      )

      const recurrencesSnapshot = await getDocs(recurrencesQuery)

      for (const recurrenceDoc of recurrencesSnapshot.docs) {
        await this.generateRecurringInvoice(orgId, recurrenceDoc.id, recurrenceDoc.data())
      }
    }
  }

  /**
   * Generate a new invoice from a recurrence
   */
  async generateRecurringInvoice(orgId, recurrenceId, recurrenceData) {
    try {
      const { templateInvoiceId, frequency, clientId, autoSend } = recurrenceData

      // Get the template invoice
      const templateRef = doc(db, `organizations/${orgId}/invoiceTemplates/${templateInvoiceId}`)
      const templateSnapshot = await getDoc(templateRef)

      if (!templateSnapshot.exists()) {
        console.error('Template invoice not found:', templateInvoiceId)
        return
      }

      const template = templateSnapshot.data()

      // Calculate next invoice dates
      const issueDate = new Date()
      const dueDate = new Date(issueDate)
      dueDate.setDate(dueDate.getDate() + (template.paymentTermsDays || 30))

      // Generate new invoice number
      const invoiceNumber = await this.generateInvoiceNumber(orgId)

      // Create new invoice
      const newInvoice = {
        ...template,
        id: undefined, // Let Firestore generate ID
        number: invoiceNumber,
        status: autoSend ? 'sent' : 'draft',
        issueDate: issueDate.toISOString().split('T')[0],
        dueDate: dueDate.toISOString().split('T')[0],
        sentAt: autoSend ? serverTimestamp() : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        recurrenceId: recurrenceId
      }

      // Add to invoices collection
      await addDoc(collection(db, `organizations/${orgId}/invoices`), newInvoice)

      // Update recurrence nextRunAt
      const nextRun = this.calculateNextRun(issueDate, frequency)
      await updateDoc(doc(db, `organizations/${orgId}/recurrences/${recurrenceId}`), {
        nextRunAt: nextRun.getTime(),
        lastRunAt: serverTimestamp()
      })

      console.log('Generated recurring invoice:', invoiceNumber)

      // Log activity
      await this.logActivity(orgId, 'recurring_invoice_generated', {
        invoiceNumber,
        recurrenceId,
        clientId
      })

    } catch (error) {
      console.error('Error generating recurring invoice:', error)
      this.logError('recurring_invoice_error', error)
    }
  }

  /**
   * Calculate next run time based on frequency
   */
  calculateNextRun(fromDate, frequency) {
    const nextRun = new Date(fromDate)

    switch (frequency) {
      case 'weekly':
        nextRun.setDate(nextRun.getDate() + 7)
        break
      case 'monthly':
        nextRun.setMonth(nextRun.getMonth() + 1)
        break
      case 'quarterly':
        nextRun.setMonth(nextRun.getMonth() + 3)
        break
      case 'yearly':
        nextRun.setFullYear(nextRun.getFullYear() + 1)
        break
      default:
        nextRun.setMonth(nextRun.getMonth() + 1) // Default to monthly
    }

    return nextRun
  }

  /**
   * Generate sequential invoice number
   */
  async generateInvoiceNumber(orgId) {
    const year = new Date().getFullYear()
    try {
      // Prefer org-scoped counters when available
      const counters = await import('./counters')
      if (counters && counters.getNextInvoiceNumber) {
        return await counters.getNextInvoiceNumber(orgId)
      }
    } catch (e) {
      console.warn('Org-scoped counters unavailable, falling back to timestamp-based invoice number', e)
    }

    // Fallback: timestamp-based number to avoid blocking recurring generation
    try {
      const timestamp = Date.now()
      const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
      return `INV-${year}-${timestamp}-${randomSuffix}`
    } catch (error) {
      console.error('Error generating invoice number fallback:', error)
      return `INV-${year}-${Date.now()}`
    }
  }

  /**
   * Process payment reminders for overdue invoices
   */
  async processPaymentReminders() {
    const now = new Date()
    const reminderDays = [7, 0, -3, -7] // 7 days before, due date, 3 and 7 days after

    const orgsSnapshot = await getDocs(collection(db, 'organizations'))

    for (const orgDoc of orgsSnapshot.docs) {
      const orgId = orgDoc.id

      for (const dayOffset of reminderDays) {
        const targetDate = new Date(now)
        targetDate.setDate(targetDate.getDate() + dayOffset)
        const targetDateStr = targetDate.toISOString().split('T')[0]

        const invoicesQuery = query(
          collection(db, `organizations/${orgId}/invoices`),
          where('status', 'in', ['sent', 'viewed', 'overdue']),
          where('dueDate', '==', targetDateStr)
        )

        const invoicesSnapshot = await getDocs(invoicesQuery)

        for (const invoiceDoc of invoicesSnapshot.docs) {
          await this.sendPaymentReminder(orgId, invoiceDoc.id, invoiceDoc.data(), dayOffset)
        }
      }
    }
  }

  /**
   * Send payment reminder using existing email service
   */
  async sendPaymentReminder(orgId, invoiceId, invoiceData, dayOffset) {
    try {
      const reminderType = dayOffset > 0 ? 'upcoming' : dayOffset === 0 ? 'due_today' : 'overdue'

      // Get organization data for email
      const orgDoc = await getDoc(doc(db, `organizations/${orgId}`))
      const orgData = orgDoc.exists() ? orgDoc.data() : {}

      // Generate reminder email
      const subject = `Payment Reminder: Invoice ${invoiceData.number}`
      const emailText = this.generateReminderEmailText(invoiceData, orgData, reminderType)

      // Try to send via existing email service
      try {
        await this.emailService._sendEmail({
          to: invoiceData.clientEmail,
          subject: subject,
          text: emailText,
          fromName: orgData?.name || 'Your Business',
          invoiceNumber: invoiceData.number
        })

        console.log(`Payment reminder sent for invoice ${invoiceData.number} (${reminderType})`)
      } catch (emailError) {
        console.log('Email service failed, creating mailto link instead')
        this.createMailtoReminder(invoiceData.clientEmail, subject, emailText)
      }

      // Log the reminder
      await this.logActivity(orgId, 'payment_reminder_sent', {
        invoiceId,
        invoiceNumber: invoiceData.number,
        reminderType,
        clientEmail: invoiceData.clientEmail,
        amount: invoiceData.total,
        currency: invoiceData.currency,
        method: 'email_service'
      })

    } catch (error) {
      console.error('Error sending payment reminder:', error)
      this.logError('payment_reminder_error', error)
    }
  }

  /**
   * Generate reminder email text
   */
  generateReminderEmailText(invoiceData, orgData, reminderType) {
    const baseText = `Dear ${invoiceData.clientName || 'Valued Customer'},\n\n`
    const amount = this.formatCurrency(invoiceData.amountDue || invoiceData.total, invoiceData.currency)

    let reminderText = ''
    switch (reminderType) {
      case 'upcoming':
        reminderText = `This is a friendly reminder that your payment of ${amount} for invoice ${invoiceData.number} is due on ${invoiceData.dueDate}.`
        break
      case 'due_today':
        reminderText = `Your payment of ${amount} for invoice ${invoiceData.number} is due today (${invoiceData.dueDate}).`
        break
      case 'overdue':
      default:
        reminderText = `Your payment of ${amount} for invoice ${invoiceData.number} was due on ${invoiceData.dueDate} and is now overdue.`
    }

    return `${baseText}${reminderText}\n\nPlease make your payment as soon as possible.\n\nThank you for your business!\n\nBest regards,\n${orgData?.name || 'Your Business'}`
  }

  /**
   * Create mailto link as fallback
   */
  createMailtoReminder(email, subject, body) {
    const encodedSubject = encodeURIComponent(subject)
    const encodedBody = encodeURIComponent(body)
    const mailtoUrl = `mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`

    if (typeof window !== 'undefined') {
      console.log('Opening mailto link for payment reminder:', mailtoUrl)
      // Could store this for user to manually send later
    }

    return mailtoUrl
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount, currency) {
    const formatter = new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: currency || 'KES',
      minimumFractionDigits: 2
    })
    return formatter.format(amount)
  }

  /**
   * Update last task check timestamp
   */
  async updateLastTaskCheck() {
    try {
      if (!this._canWriteSystem()) {
        // skip in non-dev environments to avoid permission errors
        console.debug('Skipping updateLastTaskCheck: system writes disabled')
        return
      }

      const systemTaskRef = doc(db, 'systemTasks', 'lastCheck')
      // Use setDoc with merge to create or update the document atomically.
      await setDoc(systemTaskRef, {
        lastCheck: serverTimestamp(),
        status: 'completed'
      }, { merge: true })
    } catch (error) {
      // Swallow permission errors or other write errors to avoid cascading failures.
      // Avoid calling logError here to prevent additional write attempts.
      console.warn('Unable to update systemTasks.lastCheck (may lack permission):', error && (error.message || error))
    }
  }

  /**
   * Log activity to Firestore
   */
  async logActivity(orgId, type, data) {
    try {
      await addDoc(collection(db, `organizations/${orgId}/activities`), {
        type,
        data,
        createdAt: serverTimestamp(),
        createdBy: 'system'
      })
    } catch (error) {
      console.error('Error logging activity:', error)
    }
  }

  /**
   * Log error to Firestore for monitoring
   */
  async logError(type, error) {
    try {
      // simple backoff: if recent attempts failed with permission issues, skip
      const now = Date.now()
      if (this._logWriteBlocked && (now - this._lastLogAttempt) < 5 * 60 * 1000) {
        console.warn('Skipping log write due to previous permission errors')
        return
      }

      // sanitize error payload
      const safeError = {
        message: error && (error.message || String(error)) || 'unknown',
        stack: error && error.stack ? String(error.stack) : null
      }

      const payload = {
        type,
        error: safeError.message,
        stack: safeError.stack,
        timestamp: serverTimestamp(),
        userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : 'server'
      }

      if (!this._canWriteSystem()) {
        console.warn('Skipping system log write: system writes disabled')
        return
      }
      await addDoc(collection(db, 'systemLogs'), payload)
      // reset block on success
      this._logWriteBlocked = false
      this._lastLogAttempt = now
    } catch (logError) {
      console.error('Error logging to Firestore:', logError)
      // If permission-denied, block further attempts briefly to avoid repeated 400s
      const code = logError && (logError.code || (logError?.message || ''))
      if (String(code).toLowerCase().includes('permission') || String(logError).toLowerCase().includes('permission')) {
        this._logWriteBlocked = true
        this._lastLogAttempt = Date.now()
      }
    }
  }
}

// Export singleton instance
export const taskManager = new TaskManager()
export default TaskManager
